<script lang="ts">
  /* SAVED TRIPS — the library.
   *
   * A passage is planned days before it is sailed and revised several times in
   * between. Until now nothing in this app outlived a page load: `routePlan`,
   * `autoroute` and `editor` all reset, and the only way a route ever left was
   * a GPX file you then had to find again.
   *
   * ═══ DELETE IS TWO TAPS, AND NOT A `confirm()` ═══
   *
   * There is no undo. A native `confirm()` would do the job and is the wrong
   * tool twice over: it blocks the whole page, and on a phone it is a system
   * sheet that looks like it came from the browser rather than from the app.
   * The row turns into its own confirmation instead — reversible by tapping
   * anywhere else, which is what a mis-tap on a moving deck needs.
   */
  import { downloadRouteGpx } from '@gennetay/chart-core/lib/gpx';
  import { trips, type Trip } from '@gennetay/chart-core/lib/trip.svelte';
  import Button from '../primitives/Button.svelte';
  import ButtonRow from '../primitives/ButtonRow.svelte';
  import Chip from '../primitives/Chip.svelte';
  import Empty from '../primitives/Empty.svelte';
  import SectionHeader from '../primitives/SectionHeader.svelte';

  let {
    /** Load a trip onto the chart. The host owns what that means — re-solving
     *  the route, moving the camera — because none of it is this panel's
     *  business. */
    onopen,
    /** Start a new one. Null disables the empty state's call to action, which
     *  is right for a host with no route editor. */
    onnew
  }: { onopen?: (t: Trip) => void; onnew?: () => void } = $props();

  /** Which row is asking to be confirmed. One at a time. */
  let confirming = $state<string | null>(null);

  const fmtWhen = (t: Trip) =>
    t.departT === null
      ? 'no departure set'
      : new Date(t.departT).toLocaleString([], {
          weekday: 'short',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
</script>

{#if trips.list.length === 0}
  <Empty
    title="No saved trips"
    body="Tap two points on the chart and the router will time the narrows between them. Save the result and it comes back here."
  >
    {#snippet action()}
      {#if onnew}<Button intent="primary" onclick={onnew}>Plan a trip</Button>{/if}
    {/snippet}
  </Empty>
{:else}
  <SectionHeader title="Saved">
    {#snippet aside()}<Chip>{trips.list.length}</Chip>{/snippet}
  </SectionHeader>

  <ul class="list">
    {#each trips.list as t (t.id)}
      <li class="row" class:active={trips.activeId === t.id}>
        <button
          class="main"
          onclick={() => {
            confirming = null;
            onopen?.(t);
          }}
        >
          <span class="nm">{t.name}</span>
          <span class="meta">
            {t.points.length} points · {fmtWhen(t)}
            {#if t.useCurrents}· currents{/if}
          </span>
          {#if t.notes}<span class="notes">{t.notes}</span>{/if}
        </button>

        {#if confirming === t.id}
          <!-- The row IS the confirmation. Reversible by tapping anywhere
               else, which is what a mis-tap on a moving deck needs. -->
          <div class="acts confirm">
            <span class="ask">Delete “{t.name}”?</span>
            <Button size="sm" intent="danger" onclick={() => trips.remove(t.id)}>Delete</Button>
            <Button size="sm" intent="ghost" onclick={() => (confirming = null)}>Keep</Button>
          </div>
        {:else}
          <div class="acts">
            <Button size="sm" intent="ghost" onclick={() => trips.duplicate(t.id)}>Duplicate</Button>
            <!-- The one way a route leaves this chart. There is no server to
                 write to and no business inventing an account system to get
                 one, so it leaves as a file — the door every plotter has. -->
            <Button size="sm" intent="ghost" onclick={() => downloadRouteGpx(t.name, t.points)}>
              Export GPX
            </Button>
            <Button size="sm" intent="ghost" onclick={() => (confirming = t.id)}>Delete</Button>
          </div>
        {/if}
      </li>
    {/each}
  </ul>

  {#if onnew}
    <ButtonRow>
      <Button onclick={onnew}>Plan another</Button>
    </ButtonRow>
  {/if}
{/if}

<style>
  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
  }
  .row {
    border: 1px solid var(--line);
    border-radius: var(--r-3);
    background: var(--s-1);
    overflow: hidden;
  }
  .row.active {
    border-color: var(--cy);
    background: var(--cy-weak);
  }

  .main {
    display: flex;
    flex-direction: column;
    gap: var(--sp-1);
    width: 100%;
    text-align: left;
    padding: var(--sp-4) var(--sp-5);
    background: none;
    border: 0;
    cursor: pointer;
    min-height: var(--h-touch);
  }
  .main:hover {
    background: var(--s-2);
  }
  .row.active .main:hover {
    background: none;
  }
  .nm {
    font: var(--fs-lead) / var(--lh-ui) var(--mono);
    color: var(--ink);
  }
  .meta {
    font: var(--fs-read) / var(--lh-ui) var(--sans);
    color: var(--sub);
  }
  /* The one place a skipper writes "call the marina first". Clamped rather than
     truncated: a note that clips mid-word reads as a rendering fault. */
  .notes {
    font: var(--fs-read) / var(--lh-read) var(--sans);
    color: var(--sub);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .acts {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--sp-3);
    padding: 0 var(--sp-4) var(--sp-4);
  }
  .acts.confirm {
    background: var(--red-weak);
    padding: var(--sp-4);
  }
  .ask {
    flex: 1;
    min-width: 0;
    font: var(--fs-read) / var(--lh-ui) var(--sans);
    color: var(--red);
  }
</style>
